import { CommonModule, NgFor } from "@angular/common";
import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { STEP_ROUTE_MAP } from "@zp-config/step-route-map.config";
import { AdvicePointer } from "@zp-models/product";
import { Question } from "@zp-models/question";
import { AdviceService } from "@zp-services/advice.service";
import { StateService } from "@zp-services/state.service";
import { filter, Subscription } from "rxjs";

@Component({
  imports: [CommonModule, NgFor, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: "app-receipt",
  templateUrl: "./receipt.component.html",
})
export class ReceiptComponent implements OnInit, OnDestroy {
  @Input() isAdvicePage: boolean;

  steps: Question[] = [];
  answers: Record<string, any> = {};
  private subscription!: Subscription;

  constructor(
    private stateService: StateService,
    private _adviceService: AdviceService,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.subscription = this.stateService.answers$
      .pipe(filter((answers) => answers && Object.keys(answers).length > 0))
      .subscribe((answers) => {
        const stepsConfig = this.stateService.getQuestionWizardStepsConfig();
        this.steps = Object.values(stepsConfig).filter((step) => {
          const answer = answers[step.id];
          return this.hasValidAnswer(answer);
        });
        this.answers = answers;
      });
  }

  get filteredSteps(): Question[] {
    return this.steps.filter((_, i) => i !== 0 && i !== 2);
  }

  getAdvicePointer(step: Question): AdvicePointer {
    const advicePointer = this._adviceService.getAdvicePointer(step, this.answers);
    return advicePointer;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  hasValidAnswer(answer: any): boolean {
    if (!answer) return false;

    if (typeof answer === "string") {
      return answer.trim() !== "";
    }

    if (Array.isArray(answer)) {
      return answer.length > 0;
    }

    if (typeof answer === "object") {
      return Object.values(answer).some((val) => {
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === "string") return val.trim() !== "";
        return val !== null && val !== undefined;
      });
    }

    return true;
  }

  getReceiptText(stepId: string, entryKey: string | unknown, entryValue: unknown): string {
    const labels = this.getReceiptLabel(stepId, entryKey, entryValue).map((l) =>
      this.translate.instant(l ?? "")
    );
    return labels
      .join(", ")
      .replace(/, ([^,]*)$/, ` ${this.translate.instant("RECEIPT.CONJUNCTION")} $1`);
  }

  getReceiptLabel(stepId: string, fieldName: unknown, rawValue: unknown): (string | undefined)[] {
    const step = this.steps.find((s) => s.id === stepId);
    if (!step) {
      const allStepsConfig = this.stateService.getQuestionWizardStepsConfig();
      const fallbackStep = allStepsConfig[stepId];
      if (!fallbackStep) return [String(rawValue)];
      return this.getLabelFromStepConfig(fallbackStep, fieldName, rawValue);
    }
    return this.getLabelFromStepConfig(step, fieldName, rawValue);
  }

  private getLabelFromStepConfig(
    step: Question,
    fieldName: unknown,
    rawValue: unknown
  ): (string | undefined)[] {
    const field = step.fields?.find((field: { name: string }) => field.name === fieldName);

    if (!field || !Array.isArray(field.options)) return [String(rawValue)];

    if (Array.isArray(rawValue)) {
      return rawValue.map((val) => {
        const option = field.options?.find((opt: { value: string }) => opt.value === val);
        return option ? option.receiptLabel : String(val);
      });
    }

    const option = field.options.find((opt: { value: string }) => opt.value === rawValue);

    return [option ? option.receiptLabel || option.label : String(rawValue)];
  }

  goToStep(stepId: string): void {
    const routeSegment = STEP_ROUTE_MAP[stepId];
    if (routeSegment) {
      this.router.navigate([`/keuzewizard`, routeSegment]);
    } else {
      console.warn(`[ReceiptComponent] No route found for stepId: ${stepId}`);
    }
  }
}
